import { useState, useRef, useEffect } from 'react';
import { Bell, Check, X, Info, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  type Notification,
} from '../hooks/useNotifications';
import { cn } from '@rhpc/ui';
import { useAuthStore } from '@/stores/authStore';
import { getNotificationDestination } from '@/lib/notificationLink';

const KIND_STYLE: Record<string, { bg: string; fg: string; Icon: typeof Check }> = {
  CLAIM_APPROVED: { bg: 'bg-ok-soft', fg: 'text-ok', Icon: Check },
  CLAIM_HR_APPROVED: { bg: 'bg-ok-soft', fg: 'text-ok', Icon: Check },
  CLAIM_ADMIN_APPROVED: { bg: 'bg-info-soft', fg: 'text-info', Icon: Check },
  CLAIM_FINANCE_APPROVED: { bg: 'bg-info-soft', fg: 'text-info', Icon: Check },
  CLAIM_REJECTED: { bg: 'bg-danger-soft', fg: 'text-danger', Icon: X },
  CLAIM_SUBMITTED: { bg: 'bg-info-soft', fg: 'text-info', Icon: Info },
};
const DEFAULT_KIND_STYLE = { bg: 'bg-surface-2', fg: 'text-text-3', Icon: Clock };

function kindStyleFor(type: string) {
  return KIND_STYLE[type] ?? DEFAULT_KIND_STYLE;
}

function formatTimestamp(iso: string) {
  return new Date(iso).toLocaleString('en-MY', {
    timeZone: 'Asia/Kuala_Lumpur',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PopoverRow({ notif, onClick }: { notif: Notification; onClick: () => void }) {
  const { bg, fg, Icon } = kindStyleFor(notif.type);
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 border-b border-border px-[18px] py-3 text-left last:border-b-0 hover:bg-surface-2',
        !notif.isRead && 'bg-primary-soft/50'
      )}
    >
      <div className={cn('flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full', bg, fg)}>
        <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn('text-[13px] leading-snug text-text', notif.isRead ? 'font-medium' : 'font-bold')}>
          {notif.title}
        </div>
        <div className="mt-[3px] font-mono text-xs text-text-3">{formatTimestamp(notif.createdAt)}</div>
      </div>
    </button>
  );
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);

  const { data } = useNotifications(1, 3);
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const unreadCount = data?.unreadCount ?? 0;
  const notifications = data?.notifications ?? [];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notif: Notification) => {
    if (!notif.isRead) {
      markAsRead.mutate(notif.id);
    }
    setIsOpen(false);
    const destination = getNotificationDestination(notif, role);
    if (destination) navigate(destination);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
        aria-expanded={isOpen}
        className="relative flex h-10 w-10 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-2 hover:text-text"
      >
        <Bell className="h-[19px] w-[19px]" strokeWidth={2} />
        {unreadCount > 0 && (
          <span className="absolute right-2 top-[7px] h-2 w-2 rounded-full border-2 border-surface bg-danger" />
        )}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-[46px] z-[60] w-[340px] overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-border px-[18px] py-[14px]">
            <span className="text-sm font-bold text-text">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead.mutate()}
                disabled={markAllAsRead.isPending}
                className="text-xs font-semibold text-primary-text hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-8 text-center">
              <Bell className="mb-2 h-8 w-8 text-text-3/40" />
              <p className="text-sm text-text-2">No notifications yet</p>
            </div>
          ) : (
            <div>
              {notifications.map((notif) => (
                <PopoverRow key={notif.id} notif={notif} onClick={() => handleNotificationClick(notif)} />
              ))}
            </div>
          )}

          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/notifications');
            }}
            className="block w-full py-3 text-center text-[13px] font-bold text-primary-text hover:bg-surface-2"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
